/**
 * The posting patterns (L4 of docs/plans/ledger.md): a closed set of line
 * templates that turn one money entry into two or more balanced lines.
 *
 * A posting rule (`app.posting_rule`) names one pattern and maps the pattern's
 * roles to the entry's fields, to constants, or to an account by its
 * `system_key`. The setup agent picks a pattern and maps fields; it never
 * writes lines itself. This file is the only place a line is worked out, and
 * it is pure: the server posts what it answers, and the phone runs the same
 * function only to draw a pending change (L14), never to store one.
 *
 * Amounts are exact: every figure is worked in integer minor units of the
 * entry's currency, from `toMinor` of `document-math.ts`, and leaves as a
 * decimal string. Debit is positive and credit is negative (L7). A line of
 * nought is never written, so an entry of nought posts nothing.
 *
 * Three outcomes, and the server treats each its own way (L58):
 *
 *   ok                     the lines, grouped by the day each posts on (L48)
 *   a value is missing     a role the rule maps to a field the entry left
 *                          empty: refused, naming the role (`RULE_ROLE_MISSING`,
 *                          `broken: false`), so the form can say it under
 *                          the field
 *   the rule is broken     a required role the rule never mapped, an account
 *                          that resolves to none, a choice option with no
 *                          account, a pattern not built: the entry saves as a
 *                          draft and the owner is asked to fix the rule
 *                          (`broken: true`)
 */
import { computeDocument, toMinor, type DocMathInput, type DocRounding } from '../document-math';
import { minorToDecimal } from '../doc-format';
import { carriesTaxable, stateCodeFrom, TAX_HEADS, taxAccountKey, taxSplitOf, type PostingTax, type TaxHead, type TaxLineFacts, type TaxSide } from './tax';

/* ------------------------------------------------------------- the patterns */

/** Every pattern a rule may name (L4). `tax_payment` and `charge_plan` are typed now; Phases 10 and 5 widen them. */
export const POSTING_PATTERNS = [
  'money_in',
  'money_out',
  'sale',
  'purchase',
  'receipt',
  'payment',
  'charge',
  'concession',
  'transfer',
  'refund',
  'adjustment',
  'opening_balance',
  'bill_lines',
  'tax_payment',
  'charge_plan',
  // Phase 9 (D3): a record that moves only quantity, through its rule's quantity part.
  'quantity',
] as const;
export type PostingPattern = (typeof POSTING_PATTERNS)[number];

export const isPostingPattern = (value: unknown): value is PostingPattern =>
  typeof value === 'string' && (POSTING_PATTERNS as readonly string[]).includes(value);

/** What a role reads. */
export type RoleKind = 'amount' | 'account' | 'party' | 'date' | 'flag' | 'text' | 'percent' | 'lines';

export type RoleDef = {
  readonly kind: RoleKind;
  readonly required: boolean;
  /** Used when the rule maps nothing: the receivable by its system key, for one. */
  readonly default?: RoleSource;
  /** A `lines` role: the roles each row maps. */
  readonly each?: Readonly<Record<string, RoleDef>>;
};

/**
 * Where a role's value comes from.
 *
 *   { field }            the entry's value of that field. On an account role a
 *                        reference field holds an Accounts row (L16); a
 *                        choice field names its option, and `options` maps
 *                        each option to an account (L45: Cash to Cash, UPI to
 *                        the bank, Credit to the receivable).
 *   { field, each }      a `lines` field: one value per row, read by `each`
 *   { const }            a constant: a number, a flag, a text, or on an
 *                        account role the ledger_account id itself
 *   { system_key }       an account by its system key, so a rule survives a
 *                        rename (L15)
 *   { self }             on a party role, the entry being posted: a hotel's
 *                        booking is its own party, so its room nights, its
 *                        advance and the room-bill orders that pick it all
 *                        match against it (L46, step 9.6). The server passes
 *                        the entry's id in its values under `SELF_ENTRY_KEY`.
 */
export type RoleSource =
  | {
      readonly field: string;
      readonly options?: Readonly<Record<string, RoleSource>>;
      readonly each?: Readonly<Record<string, RoleSource>>;
    }
  | { readonly const: string | number | boolean }
  | { readonly system_key: string }
  | { readonly self: true };

/** Where the values an entry posts by hold its own id, for a `{ self }` party (L46). Never a field id: those are uuids. */
export const SELF_ENTRY_KEY = '$entry';

/**
 * Which way a quantity moves (Phase 9, D3 of the brief):
 *
 *   in     came in from outside (bought, collected, made): Stock Dr, Came in Cr
 *   out    went out (sold, used): Went out Dr, Stock Cr
 *   loss   wasted or spoiled: Wastage Dr, Stock Cr
 */
export const QUANTITY_MOVES = ['in', 'out', 'loss'] as const;
export type QuantityMove = (typeof QUANTITY_MOVES)[number];

export const isQuantityMove = (value: unknown): value is QuantityMove =>
  typeof value === 'string' && (QUANTITY_MOVES as readonly string[]).includes(value);

/**
 * The quantity part of a rule (Phase 9, D3): beside its money pattern, or
 * alone (the `quantity` pattern). Each names a field by its id, as stored:
 *
 *   item    a reference to a row of the stock items list (D2)
 *   qty     a number or quantity field: how much moved
 *   unit    a unit field (a choice whose option keys are unit keys, or a
 *           text); absent, the quantity is in the item's base unit. A pack
 *           unit is turned into base units when the entry posts (1 peti = 12 pcs).
 *   lines   a `lines` field: then item, qty and unit are fields of its rows,
 *           and each row moves its own item (a bill of several items)
 */
export type QuantityPart = {
  readonly move: QuantityMove;
  readonly item: string;
  readonly qty: string;
  readonly unit?: string;
  readonly lines?: string;
};

/** One option of a both-ways choice: its pattern, the roles it lays over the rule's, and the quantity it moves (null: none). */
export type PostingChoiceOption = {
  readonly pattern?: PostingPattern;
  readonly roles?: Readonly<Record<string, RoleSource>>;
  readonly quantity?: QuantityPart | null;
};

/** A rule's map: its roles, a choice that picks the pattern for each entry (L44), the dimensions each line keeps (L41), and the quantity it moves (Phase 9). */
export type PostingMap = {
  readonly roles?: Readonly<Record<string, RoleSource>>;
  readonly by_choice?: {
    readonly field: string;
    readonly options: Readonly<Record<string, PostingChoiceOption>>;
  };
  readonly dimensions?: readonly string[];
  readonly quantity?: QuantityPart;
};

const R = (kind: RoleKind, required = false, fallback?: RoleSource): RoleDef =>
  fallback === undefined ? { kind, required } : { kind, required, default: fallback };
const KEY = (system_key: string): RoleSource => ({ system_key });

/** Every system key an account may carry (L15; the wave B contract's list), so a rule survives a rename. */
export const LEDGER_SYSTEM_KEYS = [
  'cash',
  'bank',
  'receivable',
  'payable',
  'to_sort',
  'drawings',
  'owner_capital',
  'opening_equity',
  'stock_loss',
  'sales',
  'discount_given',
  'discount_received',
  'round_off',
  'tax_out',
  'tax_out_cgst',
  'tax_out_sgst',
  'tax_out_utgst',
  'tax_out_igst',
  'tax_in',
  'tax_in_cgst',
  'tax_in_sgst',
  'tax_in_utgst',
  'tax_in_igst',
  // Phase 9 (D1): the accounts that hold quantities, made only when the workspace keeps stock.
  'stock',
  'stock_in',
  'stock_out',
  'stock_loss_qty',
] as const;
export type LedgerSystemKey = (typeof LEDGER_SYSTEM_KEYS)[number];

export const isLedgerSystemKey = (value: unknown): value is LedgerSystemKey =>
  typeof value === 'string' && (LEDGER_SYSTEM_KEYS as readonly string[]).includes(value);

/**
 * The accounts that hold quantities, not money (Phase 9, D1): Stock (on
 * hand), Came in, Went out and Wastage. Their lines carry a unit key (litre,
 * kg, pcs), never a currency, so no money figure reads them; and no money
 * pick offers them (`accountAllowed`).
 */
export const QUANTITY_SYSTEM_KEYS = ['stock', 'stock_in', 'stock_out', 'stock_loss_qty'] as const;
export type QuantitySystemKey = (typeof QUANTITY_SYSTEM_KEYS)[number];

export const isQuantitySystemKey = (value: unknown): value is QuantitySystemKey =>
  typeof value === 'string' && (QUANTITY_SYSTEM_KEYS as readonly string[]).includes(value);

/** The five account types (L15), in the order the Type field offers them. */
export const LEDGER_ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'income', 'expense'] as const;
export type LedgerAccountType = (typeof LEDGER_ACCOUNT_TYPES)[number];

export const isLedgerAccountType = (value: unknown): value is LedgerAccountType =>
  typeof value === 'string' && (LEDGER_ACCOUNT_TYPES as readonly string[]).includes(value);

/**
 * The tax roles (L43, step 10.5): the rate on the entry (a `lines` row may
 * carry its own), the price basis (`exclusive`, the default, or
 * `inclusive`), and under `in_gst` the place of supply: an override on the
 * entry, else a Tax ID or State the entry holds, else the party's.
 */
const TAX_ROLES = {
  tax: R('percent'),
  tax_basis: R('text'),
  place_of_supply: R('text'),
  tax_id: R('text'),
  state: R('text'),
} as const satisfies Record<string, RoleDef>;

/** The money-side roles a sale and a purchase share (L35, L47, L48). */
const DOCUMENT_ROLES = {
  amount: R('amount', true),
  party: R('party'),
  paid_to: R('account'),
  paid_now: R('amount'),
  paid_on: R('date'),
  /**
   * A second and a third part paid at once, each to its own place: a day's
   * takings kept as a Cash amount and a UPI amount (M1, 30 Oct). Each posts
   * as `paid_now` does, on its own day when it names one.
   */
  paid_now_2: R('amount'),
  paid_to_2: R('account'),
  paid_on_2: R('date'),
  paid_now_3: R('amount'),
  paid_to_3: R('account'),
  paid_on_3: R('date'),
  payments: { kind: 'lines', required: false, each: { amount: R('amount', true), account: R('account', true), on: R('date') } },
  /**
   * The bill's item lines, read for tax only (L43, L49): each row's taxable
   * value (its amount, or its quantity times its rate) and its own rate and
   * code. When a row carries a rate the income (or the cost) is the rows'
   * taxable value and the tax is worked out row by row, as the invoice works
   * it; with no rate anywhere the entry posts its amount as before.
   */
  lines: { kind: 'lines', required: false, each: { amount: R('amount'), qty: R('text'), rate: R('text'), tax: R('percent'), hsn: R('text') } },
  /**
   * The tax the bill works out itself (G18): a GST (5%) or GST amount field,
   * nought when its GST bill toggle is off. When mapped it is the tax posted,
   * before any rate: split CGST and SGST inside the state and IGST between
   * states, its rate (`tax`, else the amount over the taxable value) kept only
   * for the tax summary. `taxable` is the value before it (a Subtotal); with
   * none, the amount less the tax.
   */
  tax_amount: R('amount'),
  taxable: R('amount'),
  discount: R('amount'),
  round_off: R('amount'),
  round_off_account: R('account', false, KEY('round_off')),
  charge: R('amount'),
  charge_account: R('account'),
  ...TAX_ROLES,
  /** The rounding of the grand total, as a tax invoice's template sets it: `none`, `rupee` or `ten_paise`. */
  rounding: R('text'),
  hsn: R('text'),
} as const satisfies Record<string, RoleDef>;

/** Each pattern's roles. `main` is the line a record's headline reads (L50). */
export const PATTERN_ROLES: Readonly<Record<PostingPattern, Readonly<Record<string, RoleDef>>>> = {
  money_in: {
    amount: R('amount', true),
    account: R('account', true),
    paid_to: R('account', true),
    party: R('party'),
    against_dues: R('flag'),
    receivable: R('account', false, KEY('receivable')),
  },
  money_out: {
    amount: R('amount', true),
    account: R('account', true),
    paid_from: R('account', true),
    party: R('party'),
    against_dues: R('flag'),
    payable: R('account', false, KEY('payable')),
    ...TAX_ROLES,
  },
  sale: {
    ...DOCUMENT_ROLES,
    income: R('account', true),
    receivable: R('account', false, KEY('receivable')),
    discount_account: R('account', false, KEY('discount_given')),
    cost: R('amount'),
    cost_account: R('account'),
    cost_payable: R('account', false, KEY('payable')),
    cost_party: R('party'),
    commission: R('amount'),
    commission_rate: R('percent'),
    commission_account: R('account'),
    commission_payable: R('account'),
  },
  purchase: {
    ...DOCUMENT_ROLES,
    expense: R('account', true),
    payable: R('account', false, KEY('payable')),
    discount_account: R('account', false, KEY('discount_received')),
  },
  receipt: {
    amount: R('amount', true),
    paid_to: R('account', true),
    party: R('party'),
    receivable: R('account', false, KEY('receivable')),
  },
  payment: {
    amount: R('amount', true),
    paid_from: R('account', true),
    party: R('party'),
    payable: R('account', false, KEY('payable')),
  },
  charge: {
    amount: R('amount', true),
    income: R('account', true),
    amount_2: R('amount'),
    income_2: R('account'),
    amount_3: R('amount'),
    income_3: R('account'),
    discount: R('amount'),
    discount_percent: R('percent'),
    discount_account: R('account', false, KEY('discount_given')),
    party: R('party'),
    receivable: R('account', false, KEY('receivable')),
  },
  concession: {
    amount: R('amount', true),
    account: R('account', false, KEY('discount_given')),
    party: R('party'),
    receivable: R('account', false, KEY('receivable')),
    for_period: R('date'),
    ...TAX_ROLES,
  },
  transfer: {
    amount: R('amount', true),
    from: R('account', true),
    to: R('account', true),
  },
  refund: {
    amount: R('amount', true),
    paid_from: R('account', true),
    party: R('party'),
    account: R('account', false, KEY('receivable')),
    ...TAX_ROLES,
  },
  adjustment: {
    amount: R('amount', true),
    account: R('account', true),
    against: R('account', true),
    reason: R('text', true),
    party: R('party'),
  },
  opening_balance: {
    amount: R('amount', true),
    account: R('account', true),
    party: R('party'),
    equity: R('account', false, KEY('opening_equity')),
  },
  bill_lines: {
    lines: {
      kind: 'lines',
      required: true,
      // A row's value is its amount, or its quantity times its rate, multiplied as a tax invoice multiplies them (L49).
      each: { amount: R('amount'), qty: R('text'), rate: R('text'), account: R('account', true), discount: R('amount'), tax: R('percent'), hsn: R('text') },
    },
    direction: R('text'),
    party: R('party'),
    counter: R('account'),
    paid_to: R('account'),
    paid_now: R('amount'),
    paid_on: R('date'),
    discount: R('amount'),
    discount_account: R('account'),
    round_off: R('amount'),
    round_off_account: R('account', false, KEY('round_off')),
    ...TAX_ROLES,
    rounding: R('text'),
  },
  tax_payment: {
    // One tax (single, sales_tax): what was collected, and the credit set off.
    output_amount: R('amount'),
    output_account: R('account', false, KEY('tax_out')),
    input_amount: R('amount'),
    input_account: R('account', false, KEY('tax_in')),
    // GST (in_gst): each head collected, and each head's credit set off (L43).
    output_cgst: R('amount'),
    output_sgst: R('amount'),
    output_utgst: R('amount'),
    output_igst: R('amount'),
    input_cgst: R('amount'),
    input_sgst: R('amount'),
    input_utgst: R('amount'),
    input_igst: R('amount'),
    paid_from: R('account', true),
  },
  // L23, Phase 5: the plan on a party list. It posts nothing itself; its run
  // writes entries into the Charges record, and each posts through that
  // record's own `charge` rule. Its amount and percent roles read a field of
  // the plan row the party picks (`plan`), or of the party row itself.
  charge_plan: {
    plan: R('party'),
    charges_record: R('text', true),
    amount: R('amount', true),
    income: R('account', true),
    amount_2: R('amount'),
    income_2: R('account'),
    amount_3: R('amount'),
    income_3: R('account'),
    discount_percent: R('percent'),
    discount_account: R('account'),
    /*
     * A discount by cycle (B1 of docs/plans/gym-membership.md): the value
     * (`discount_value`, an amount or a percent as the `discount_kind` select
     * says; a percent when there is none), how long it lasts (`discount_for`,
     * a select keyed first, cycles or every), the cycles this one included
     * (`discount_cycles`) and the day they are counted from (`discount_from`),
     * fields of the party row. `charges_cycle`: the Charges record's text field
     * the run writes the cycle into ("2 of 3").
     */
    discount_value: R('amount'),
    discount_kind: R('text'),
    discount_for: R('text'),
    discount_cycles: R('text'),
    discount_from: R('date'),
    charges_cycle: R('text'),
    cadence: R('text'),
    /** `full` or `by_days`: what a party who joins after the period starts pays for it. */
    first_period: R('text'),
    /** A date field of the plan list: a plan row's amounts hold from that day (the version in effect at the period start). */
    effective_from: R('date'),
    /** A text field of the Charges record the run fills with the period ("June 2026"). */
    charges_for: R('text'),
    /**
     * The day the plan was put on the list, a const YYYY-MM-DD: a run bills on
     * its own only a period that starts on or after it, so a plan installed in
     * the middle of a period leaves that period to Run now (owner, 29 Oct).
     */
    installed_on: R('date'),
  },
  // Phase 9 (D3): no money roles; its quantity part says what moves.
  quantity: {},
};

/** Whether money from this pattern comes in or goes out: what the grant fences in_only and out_only read (L50). */
export const PATTERN_DIRECTION: Readonly<Record<PostingPattern, 1 | -1 | 0>> = {
  money_in: 1,
  money_out: -1,
  sale: 1,
  purchase: -1,
  receipt: 1,
  payment: -1,
  charge: 1,
  concession: -1,
  transfer: 0,
  refund: -1,
  adjustment: 0,
  opening_balance: 0,
  bill_lines: 1,
  tax_payment: -1,
  charge_plan: 0,
  quantity: 0,
};

/* ------------------------------------------------------------ the inputs */

/** What the chart of accounts holds that a template reads: the accounts, by system key and by their Accounts row. */
export type LedgerChart = {
  /** The unit the money lines carry: the entry's currency code. */
  readonly unit: string;
  /** Its minor-unit digits: 2 for INR, 3 for KWD. */
  readonly digits: number;
  /** system_key to ledger_account id. */
  readonly bySystemKey: Readonly<Record<string, string>>;
  /** An Accounts row's entry id (lower case) to its ledger_account id (L15). */
  readonly byEntry: Readonly<Record<string, string>>;
  /** Every ledger_account id. A `{ const }` on an account role must name one. */
  readonly accounts: ReadonlySet<string>;
  /** The accounts whose lines get matched (L21): a line on one needs a party (L37). */
  readonly openItem: ReadonlySet<string>;
  /**
   * The stock items an entry's quantity part names (Phase 9, D2), by their
   * row's entry id in lower case: each item's base unit key and its one pack
   * size. The server reads them before it builds; an item not here is no row
   * of a stock items list.
   */
  readonly items?: Readonly<Record<string, StockItemFacts>>;
  /** A unit choice's option ids (lower case) to the unit keys they name: the server reads them off the field. */
  readonly unitWords?: Readonly<Record<string, string>>;
};

/** One stock item as a quantity line reads it (D2): its base unit, and how many base units its one pack holds. */
export type StockItemFacts = {
  readonly unit: string;
  readonly pack: { readonly unit: string; readonly size: string } | null;
};

/** The entry being posted: its values by field id; a `lines` field holds its rows' values. */
export type LedgerValues = Readonly<Record<string, unknown>>;

export type BuildOptions = {
  /** The day the entry posts on, `YYYY-MM-DD`: its time axis, or the day it was saved. */
  readonly on: string;
  /** Kept on every line: the operating unit, always, when the entry has one (L41). */
  readonly dimensions?: Readonly<Record<string, unknown>>;
  /** The workspace's tax (L43). Without it, or under `none`, no line carries tax. */
  readonly tax?: PostingTax;
};

/* ----------------------------------------------------------- the outputs */

/** One line a template wrote. */
export type DraftLine = {
  readonly account: string;
  /** Exact decimal; debit positive, credit negative. Never nought. */
  readonly amount: string;
  readonly unit: string;
  readonly party: string | null;
  /** The day this line posts on (L48: a payment line keeps its own date). */
  readonly on: string;
  /** The role that wrote it: the Money effect words and the accountant view read it. */
  readonly role: string;
  /** The line a record's headline reads (L50). One per posting. */
  readonly main: boolean;
  readonly dimensions: Readonly<Record<string, unknown>>;
  /** The stock item a quantity line moves (Phase 9): its row's entry id. Absent or null on a money line. */
  readonly item?: string | null;
  /**
   * True on a line that belonged on an account whose lines get matched but
   * named no party, so it went to To sort instead (L37): the server asks for
   * the party in Needs you.
   */
  readonly noParty?: true;
};

export type LedgerProblemCode =
  | 'RULE_ROLE_MISSING'
  | 'RULE_ROLE_UNKNOWN'
  | 'RULE_ACCOUNT_UNKNOWN'
  | 'RULE_CHOICE_UNMAPPED'
  /** A party role reads a value that is no row of any list: the server finds it, before the insert. */
  | 'RULE_PARTY_UNKNOWN'
  | 'RULE_PATTERN_UNKNOWN'
  | 'POSTING_UNBALANCED'
  | 'POSTING_ONE_LINE';

export type LedgerProblem = {
  readonly code: LedgerProblemCode;
  /** The role it is about, when it is about one. */
  readonly role: string | null;
  /** The field the role reads, when the problem is an empty field. */
  readonly fieldId: string | null;
  /** True when the rule itself is at fault (L58): the entry saves as a draft. False: the entry is refused. */
  readonly broken: boolean;
  /** The unit that does not balance, for POSTING_UNBALANCED. */
  readonly unit?: string;
};

export type BuildResult =
  | { readonly ok: true; readonly pattern: PostingPattern; readonly lines: readonly DraftLine[] }
  | { readonly ok: false; readonly problem: LedgerProblem };

/* ------------------------------------------------------------- the reading */

class Stop extends Error {
  constructor(readonly problem: LedgerProblem) {
    super(problem.code);
  }
}

const stop = (code: LedgerProblemCode, role: string | null, fieldId: string | null, broken: boolean): never => {
  throw new Stop({ code, role, fieldId, broken });
};

const isEmpty = (value: unknown): boolean =>
  value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

const asId = (value: unknown): string | null => {
  if (typeof value === 'string' && value !== '') return value;
  // A reference read back as { id, ... } or a one-member set.
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  if (typeof value === 'object' && value !== null && typeof (value as { id?: unknown }).id === 'string') {
    return (value as { id: string }).id;
  }
  return null;
};

const asDay = (value: unknown): string | null =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;

/** What a template reads its roles through. */
type Reader = {
  readonly account: (role: string) => string | null;
  readonly amount: (role: string) => bigint | null;
  readonly percent: (role: string) => bigint | null;
  readonly party: (role: string) => string | null;
  readonly date: (role: string) => string | null;
  readonly flag: (role: string) => boolean;
  readonly text: (role: string) => string | null;
  readonly rows: (role: string) => { readonly read: Reader }[];
  readonly maps: (role: string) => boolean;
};

/** Reads the roles of one pattern from one set of values. */
const readerOf = (
  defs: Readonly<Record<string, RoleDef>>,
  roles: Readonly<Record<string, RoleSource>>,
  values: LedgerValues,
  chart: LedgerChart,
): Reader => {
  const sourceOf = (role: string): RoleSource | undefined => roles[role] ?? defs[role]?.default;

  /** The raw value of a role, or undefined when the rule maps nothing and the role may be left out. */
  const raw = (role: string, source: RoleSource | undefined = sourceOf(role)): { value: unknown; fieldId: string | null } | undefined => {
    const def = defs[role];
    if (source === undefined) {
      if (def?.required === true) stop('RULE_ROLE_MISSING', role, null, true);
      return undefined;
    }
    if ('const' in source) return { value: source.const, fieldId: null };
    if ('system_key' in source) return { value: source, fieldId: null };
    if ('self' in source) {
      // The entry itself (L46): read only by a party role. Without its id (a check with no entry) the role is left out.
      const own = def?.kind === 'party' ? values[SELF_ENTRY_KEY] : undefined;
      return isEmpty(own) ? undefined : { value: own, fieldId: null };
    }
    const value = values[source.field];
    if (isEmpty(value)) {
      if (def?.required === true) stop('RULE_ROLE_MISSING', role, source.field, false);
      return undefined;
    }
    return { value, fieldId: source.field };
  };

  const accountFrom = (role: string, source: RoleSource): string => {
    if ('system_key' in source) {
      return chart.bySystemKey[source.system_key] ?? stop('RULE_ACCOUNT_UNKNOWN', role, null, true);
    }
    if ('const' in source) {
      const id = String(source.const);
      return chart.accounts.has(id) ? id : chart.byEntry[id.toLowerCase()] ?? stop('RULE_ACCOUNT_UNKNOWN', role, null, true);
    }
    return stop('RULE_ACCOUNT_UNKNOWN', role, 'field' in source ? source.field : null, true);
  };

  const account = (role: string): string | null => {
    const source = sourceOf(role);
    const got = raw(role, source);
    if (got === undefined) return null;
    if (source !== undefined && 'field' in source) {
      if (source.options !== undefined) {
        // A choice of mode (L45): each option names its account.
        const key = asId(got.value) ?? String(got.value);
        const option = source.options[key];
        if (option === undefined) return stop('RULE_CHOICE_UNMAPPED', role, source.field, true);
        return accountFrom(role, option);
      }
      const id = asId(got.value);
      if (id === null) return stop('RULE_ACCOUNT_UNKNOWN', role, source.field, true);
      return chart.byEntry[id.toLowerCase()] ?? stop('RULE_ACCOUNT_UNKNOWN', role, source.field, true);
    }
    return accountFrom(role, source!);
  };

  const amount = (role: string): bigint | null => {
    const got = raw(role);
    if (got === undefined) return null;
    const minor = toMinor(got.value as string | number, chart.digits);
    if (minor === null) return stop('RULE_ROLE_MISSING', role, got.fieldId, got.fieldId === null);
    return minor;
  };

  /** A rate, as hundredths of a percent: 10 gives 1000n, 12.5 gives 1250n. */
  const percent = (role: string): bigint | null => {
    const got = raw(role);
    if (got === undefined) return null;
    return toMinor(got.value as string | number, 2);
  };

  const party = (role: string): string | null => {
    const got = raw(role);
    return got === undefined ? null : asId(got.value);
  };

  const date = (role: string): string | null => {
    const got = raw(role);
    return got === undefined ? null : asDay(got.value);
  };

  const flag = (role: string): boolean => {
    const got = raw(role);
    return got !== undefined && (got.value === true || got.value === 'true' || got.value === 1 || got.value === 'yes');
  };

  const text = (role: string): string | null => {
    const got = raw(role);
    return got === undefined ? null : String(got.value);
  };

  /** A `lines` role: each row read by its own sub-roles. */
  const rows = (role: string): { readonly read: Reader }[] => {
    const source = sourceOf(role);
    const got = raw(role, source);
    if (got === undefined || source === undefined || !('field' in source)) return [];
    const each = defs[role]?.each ?? {};
    const sub = source.each ?? {};
    const list = Array.isArray(got.value) ? (got.value as unknown[]) : [];
    return list
      .filter((row): row is LedgerValues => typeof row === 'object' && row !== null)
      .map((row) => ({ read: readerOf(each, sub, row, chart) }));
  };

  /** Whether the rule maps a role at all, its value aside. */
  const maps = (role: string): boolean => roles[role] !== undefined;

  return { account, amount, percent, party, date, flag, text, rows, maps };
};

/* --------------------------------------------------------------- the build */

type Working = {
  account: string;
  minor: bigint;
  party: string | null;
  on: string;
  role: string;
  main: boolean;
  /** Kept on this line only, beside the entry's dimensions: a tax line's facts (`dimensions.tax`). */
  extra?: Readonly<Record<string, unknown>>;
};

/** What a template needs beyond its roles: the chart, for the tax accounts, and the workspace's tax. */
type Context = { readonly chart: LedgerChart; readonly tax: PostingTax | undefined };

/** One line of a document before tax: its value (before its own discount), that discount, its rate and code. */
type TaxInput = { readonly minor: bigint; readonly discount: bigint; readonly rate: string | null; readonly hsn: string | null };

/** A document's figures under tax, in minor units, as `computeDocument` worked them. */
type Taxed = {
  /** What each input line's income or expense line posts: its taxable value with its discounts added back, so the discount posts on its own. */
  readonly lineValues: readonly bigint[];
  /** The bill discount, as shared across the lines. */
  readonly discount: bigint;
  /** What the charge posts: its taxable value. */
  readonly charge: bigint;
  /** The tax lines, each a positive amount; the template gives the sign. */
  readonly heads: readonly Working[];
  /** The round off the template's rounding gives, or null when the rule names none. */
  readonly roundOff: bigint | null;
};

const ROUNDINGS: readonly DocRounding[] = ['none', 'rupee', 'ten_paise'];

/**
 * The tax on one entry's lines, worked out by `computeDocument`, the same
 * call a tax invoice makes (L43, L49). Null when nothing is taxed: no tax
 * context, the regime `none`, or no rate on any line.
 *
 * `side` says which accounts the tax goes to: tax collected on a sale, tax
 * you can claim on a purchase. Under `sales_tax` there is no input credit, so
 * a purchase's tax stays in what it cost.
 */
const taxOf = (
  ctx: Context,
  read: Reader,
  side: TaxSide,
  inputs: readonly TaxInput[],
  opts: { readonly document: boolean; readonly on: string },
): Taxed | null => {
  const tax = ctx.tax;
  if (tax === undefined || tax.regime === 'none' || inputs.length === 0) return null;
  if (inputs.every((one) => one.rate === null || one.rate.trim() === '')) return null;
  const partyId = read.party('party')?.toLowerCase() ?? null;
  const facts = partyId === null ? undefined : tax.parties?.[partyId];
  const ownTaxId = read.text('tax_id');
  const place =
    stateCodeFrom({ state: read.text('place_of_supply') }) ??
    stateCodeFrom({ taxId: ownTaxId, state: read.text('state') }) ??
    facts?.state ??
    null;
  const split = taxSplitOf({ regime: tax.regime, businessState: tax.businessState, place });
  if (split === 'none') return null;
  const digits = ctx.chart.digits;
  const dec = (minor: bigint): string => minorToDecimal(minor, digits);
  const roundingText = opts.document ? read.text('rounding') : null;
  const rounding = ROUNDINGS.find((one) => one === roundingText) ?? 'none';
  const discount = opts.document ? (read.amount('discount') ?? 0n) : 0n;
  const charge = opts.document ? (read.amount('charge') ?? 0n) : 0n;
  const input: DocMathInput = {
    lines: inputs.map((one) => ({
      qty: null,
      rate: dec(one.minor),
      discount: one.discount === 0n ? null : dec(one.discount),
      amount: null,
      taxable: null,
      taxRate: one.rate,
      hsn: one.hsn,
    })),
    priceBasis: read.text('tax_basis') === 'inclusive' ? 'inclusive' : 'exclusive',
    split,
    rounding,
    billDiscount: discount === 0n ? null : dec(discount),
    charges: charge === 0n ? [] : [{ key: 'charge', amount: dec(charge), gst: 'highest', rate: null }],
    paid: null,
    digits,
  };
  const figures = computeDocument(input);
  // A rate that is not a number is the person's to fix, under the field.
  if (figures.problems.some((one) => one.code === 'not_a_number')) stop('RULE_ROLE_MISSING', 'tax', null, false);
  const credit = side === 'output' || tax.regime !== 'sales_tax';
  const big = (n: number): bigint => BigInt(n);
  const lineValues = figures.lines.map((line, i) => big(credit ? line.taxable : line.total) + big(line.discountShare) + inputs[i]!.discount);
  const chargeValue = figures.charges.reduce((sum, one) => sum + big(credit ? one.taxable : one.total), 0n);
  const heads: Working[] = [];
  if (credit) {
    const partyTaxId = ownTaxId ?? facts?.taxId ?? null;
    for (const group of figures.byHsn) {
      for (const head of TAX_HEADS) {
        const minor = big(group[head]);
        if (minor === 0n) continue;
        const key = taxAccountKey(side, head);
        const account = ctx.chart.bySystemKey[key] ?? stop('RULE_ACCOUNT_UNKNOWN', 'tax', null, true);
        const kept: TaxLineFacts = {
          rate_bp: group.rateBp,
          hsn: group.hsn,
          head,
          ...(carriesTaxable(head) ? { taxable: dec(big(group.taxable)) } : {}),
          party_tax_id: partyTaxId,
          place,
        };
        heads.push({ account, minor, party: null, on: opts.on, role: 'tax', main: false, extra: { tax: kept } });
      }
    }
  }
  return {
    lineValues,
    discount: big(figures.billDiscount),
    charge: chargeValue,
    heads,
    roundOff: rounding === 'none' ? null : big(figures.roundOff),
  };
};

/** Where a document's tax is supplied: an override on the entry, else a Tax ID or State it holds, else the party's. */
const placeOf = (ctx: Context, read: Reader): { readonly place: string | null; readonly partyTaxId: string | null } => {
  const tax = ctx.tax;
  const partyId = read.party('party')?.toLowerCase() ?? null;
  const facts = partyId === null || tax === undefined ? undefined : tax.parties?.[partyId];
  const ownTaxId = read.text('tax_id');
  const place = stateCodeFrom({ state: read.text('place_of_supply') }) ?? stateCodeFrom({ taxId: ownTaxId, state: read.text('state') }) ?? facts?.state ?? null;
  return { place, partyTaxId: ownTaxId ?? facts?.taxId ?? null };
};

/**
 * The tax a bill worked out itself, posted as it is (G18): the taxable value
 * to income or the cost, the tax split by place of supply, CGST taking the
 * smaller half of an odd paisa. Null under no tax, or a tax of nought (a bill
 * whose GST toggle is off). Under `sales_tax` a purchase's tax stays in what
 * it cost. The rate kept for the summary is the rule's, else the tax over the
 * taxable value.
 */
const taxOfAmount = (ctx: Context, read: Reader, side: TaxSide, taxable: bigint, taxAmount: bigint, on: string): Taxed | null => {
  const tax = ctx.tax;
  if (tax === undefined || tax.regime === 'none' || taxAmount === 0n) return null;
  const { place, partyTaxId } = placeOf(ctx, read);
  const split = taxSplitOf({ regime: tax.regime, businessState: tax.businessState, place });
  if (split === 'none') return null;
  const discount = read.amount('discount') ?? 0n;
  const charge = read.amount('charge') ?? 0n;
  const credit = side === 'output' || tax.regime !== 'sales_tax';
  if (!credit) return { lineValues: [taxable + taxAmount], discount, charge, heads: [], roundOff: null };
  // In basis points, as `computeDocument` keeps a rate: 5% is 500.
  const said = read.percent('tax');
  const rate = said !== null ? Number(said) : taxable === 0n ? 0 : Number((taxAmount * 20000n + taxable) / (2n * taxable));
  const halves = (whole: bigint): [bigint, bigint] => [whole / 2n, whole - whole / 2n];
  const parts: Array<[TaxHead, bigint]> =
    split === 'inter' ? [['igst', taxAmount]] : split === 'single' ? [['single', taxAmount]] : (() => {
      const [first, second] = halves(taxAmount);
      return [['cgst', first], [split === 'intra_ut' ? 'utgst' : 'sgst', second]] as Array<[TaxHead, bigint]>;
    })();
  const heads: Working[] = parts.map(([head, minor]) => {
    const account = ctx.chart.bySystemKey[taxAccountKey(side, head)] ?? stop('RULE_ACCOUNT_UNKNOWN', 'tax_amount', null, true);
    const kept: TaxLineFacts = {
      rate_bp: rate,
      hsn: read.text('hsn'),
      head,
      ...(carriesTaxable(head) ? { taxable: minorToDecimal(taxable, ctx.chart.digits) } : {}),
      party_tax_id: partyTaxId,
      place,
    };
    return { account, minor, party: null, on, role: 'tax', main: false, extra: { tax: kept } };
  });
  return { lineValues: [taxable], discount, charge, heads, roundOff: null };
};

/**
 * A row's quantity times its rate, in minor units, by `computeDocument`'s own
 * multiplication (rounded once), or null when either is missing.
 */
const rowValue = (qty: string | null, rate: string | null, digits: number): bigint | null => {
  if (rate === null || rate.trim() === '') return null;
  const figures = computeDocument({
    lines: [{ qty, rate, discount: null, amount: null, taxable: null, taxRate: null }],
    priceBasis: 'exclusive',
    split: 'none',
    rounding: 'none',
    billDiscount: null,
    charges: [],
    paid: null,
    digits,
  });
  if (figures.problems.length > 0) stop('RULE_ROLE_MISSING', 'lines', null, false);
  return BigInt(figures.lines[0]!.base);
};

/** The rate a row or the entry names, as text, or null. */
const rateOf = (read: Reader): string | null => read.text('tax');

const percentOf = (base: bigint, hundredths: bigint): bigint => {
  // base x rate / 100, rounded half away from zero; the rate carries two decimals.
  const n = base * hundredths;
  const d = 10000n;
  const q = n / d;
  const r = n % d;
  const twice = (r < 0n ? -r : r) * 2n;
  return twice >= d ? (n < 0n ? q - 1n : q + 1n) : q;
};

/**
 * The tax on a sale or a purchase (L43): row by row when its item lines carry
 * a rate (a trader's bill of rice at 5% and oil at 18%), else on the entry's
 * amount at the entry's rate. `value` is what the income or the cost line
 * posts: the taxable value, or the amount when nothing is taxed.
 */
const documentTax = (ctx: Context, read: Reader, side: TaxSide, amount: bigint, on: string): { readonly value: bigint; readonly taxed: Taxed | null } => {
  // The tax the bill worked out, when the rule reads it: posted as it is, never worked out again from a rate.
  if (read.maps('tax_amount')) {
    const taxAmount = read.amount('tax_amount') ?? 0n;
    const taxable = read.amount('taxable') ?? amount - taxAmount;
    const taxed = taxOfAmount(ctx, read, side, taxable, taxAmount, on);
    return { value: taxed?.lineValues[0] ?? taxable, taxed };
  }
  const inputs: TaxInput[] = [];
  for (const row of read.rows('lines')) {
    const minor = row.read.amount('amount') ?? rowValue(row.read.text('qty'), row.read.text('rate'), ctx.chart.digits);
    if (minor === null || minor === 0n) continue;
    inputs.push({ minor, discount: 0n, rate: row.read.text('tax') ?? rateOf(read), hsn: row.read.text('hsn') ?? read.text('hsn') });
  }
  if (inputs.some((one) => one.rate !== null && one.rate.trim() !== '')) {
    const taxed = taxOf(ctx, read, side, inputs, { document: true, on });
    if (taxed !== null) return { value: taxed.lineValues.reduce((sum, one) => sum + one, 0n), taxed };
  }
  const taxed = taxOf(ctx, read, side, [{ minor: amount, discount: 0n, rate: rateOf(read), hsn: read.text('hsn') }], { document: true, on });
  return { value: taxed?.lineValues[0] ?? amount, taxed };
};

/** The parts paid at once a document may map: what was paid, where it went, and the day. */
const PAID_PARTS = [
  ['paid_now', 'paid_to', 'paid_on'],
  ['paid_now_2', 'paid_to_2', 'paid_on_2'],
  ['paid_now_3', 'paid_to_3', 'paid_on_3'],
] as const;

/** A sale's or a purchase's lines: `s` is 1 for a sale, -1 for a purchase. */
const documentLines = (
  s: 1n | -1n,
  read: Reader,
  on: string,
  out: Working[],
  opts: {
    readonly kind: 'sale' | 'purchase' | 'bill';
    readonly counterRole: string;
    readonly gross: readonly Working[];
    /** The figures under tax: the gross lines already carry their taxable values. */
    readonly taxed?: Taxed | null;
  },
): void => {
  const counter = read.account(opts.counterRole)!;
  const party = read.party('party');
  const taxed = opts.taxed ?? null;
  const discount = taxed === null ? (read.amount('discount') ?? 0n) : taxed.discount;
  const charge = taxed === null ? (read.amount('charge') ?? 0n) : taxed.charge;
  const roundOff = taxed?.roundOff ?? read.amount('round_off') ?? 0n;
  const start = out.length;
  out.push(...opts.gross);
  // Tax collected is a credit on a sale; tax you can claim is a debit on a purchase.
  for (const head of taxed?.heads ?? []) out.push({ ...head, minor: -s * head.minor });
  if (discount !== 0n) {
    const account = read.account('discount_account') ?? stop('RULE_ROLE_MISSING', 'discount_account', null, true);
    out.push({ account, minor: s * discount, party: null, on, role: 'discount_account', main: false });
  }
  if (charge !== 0n) {
    const account = read.account('charge_account') ?? stop('RULE_ROLE_MISSING', 'charge_account', null, true);
    out.push({ account, minor: -s * charge, party: null, on, role: 'charge_account', main: false });
  }
  if (roundOff !== 0n) {
    out.push({ account: read.account('round_off_account')!, minor: -s * roundOff, party: null, on, role: 'round_off_account', main: false });
  }
  // The counter takes what balances every line before it: what the party owes, or is owed.
  const total = -s * out.slice(start).reduce((sum, line) => sum + line.minor, 0n);
  const counterAt = out.length;
  out.push({ account: counter, minor: s * total, party, on, role: opts.counterRole, main: true });

  // What was paid at once (L35, L48): a field (or up to three, each to its own place), the rows of a Payments kit,
  // or a mode that is not the counter (L45).
  const payments: { minor: bigint; account: string; on: string }[] = [];
  const paidTo = read.account('paid_to');
  // A part typed as 0 says nothing was paid at once: the mode below then never means paid in full.
  let saidNone = false;
  for (const [nowRole, toRole, onRole] of PAID_PARTS) {
    const minor = read.amount(nowRole);
    if (minor === 0n) saidNone = true;
    if (minor === null || minor === 0n) continue;
    const account = nowRole === 'paid_now' ? paidTo : read.account(toRole);
    payments.push({ minor, account: account ?? stop('RULE_ROLE_MISSING', toRole, null, true), on: read.date(onRole) ?? on });
  }
  if (opts.kind !== 'bill') {
    for (const row of read.rows('payments')) {
      const minor = row.read.amount('amount');
      if (minor === null || minor === 0n) continue;
      payments.push({ minor, account: row.read.account('account')!, on: row.read.date('on') ?? on });
    }
  }
  // A mode with nothing paid named says the whole was paid there (L45). Payment lines are where a bill is paid: with none
  // added, nothing is paid yet, whatever its mode says (a booking with no advance, M2). An advance typed as 0 says the
  // same: nothing was paid (trucks, 31 Oct: a trip with Advance 0 counted as paid in full by its mode, its party's
  // Pending 0). A Paid now left blank keeps the old reading: a cash sale needs no amount typed twice.
  if (payments.length === 0 && paidTo !== null && paidTo !== counter && !read.maps('payments') && !saidNone) {
    payments.push({ minor: total, account: paidTo, on });
  }

  const paid = payments.reduce((sum, one) => sum + one.minor, 0n);
  if (party === null && payments.length > 0 && paid === total && payments.every((one) => one.on === on)) {
    // Paid in full on the day by nobody named: no party's dues to clear, so the money lands where the
    // counter would have. A named party keeps the pair, matched to each other in this posting, so its
    // Billed and Received count every sale (L33).
    out.splice(counterAt, 1);
    // Each part is the entry's money, so a day's Cash 5,000 and UPI 3,000 head the record as 8,000.
    payments.forEach((one) => out.push({ account: one.account, minor: s * one.minor, party: null, on, role: 'paid_to', main: true }));
    return;
  }
  for (const one of payments) {
    out.push({ account: one.account, minor: s * one.minor, party: null, on: one.on, role: 'paid_to', main: false });
    out.push({ account: counter, minor: -s * one.minor, party, on: one.on, role: opts.counterRole, main: false });
  }
};

/** One pattern's lines, before the shared steps (dimensions, To sort, the nought lines). */
const templateLines = (pattern: PostingPattern, read: Reader, on: string, ctx: Context): Working[] => {
  const out: Working[] = [];
  const line = (account: string, minor: bigint, role: string, main: boolean, party: string | null = null, day = on): void => {
    out.push({ account, minor, party, on: day, role, main });
  };
  switch (pattern) {
    case 'money_in': {
      const amount = read.amount('amount')!;
      const party = read.party('party');
      line(read.account('paid_to')!, amount, 'paid_to', true);
      // "Is this against Kabir's dues?" (L24): a yes clears the receivable instead of counting income twice.
      if (read.flag('against_dues')) line(read.account('receivable')!, -amount, 'receivable', false, party);
      else line(read.account('account')!, -amount, 'account', false, party);
      return out;
    }
    case 'money_out': {
      const amount = read.amount('amount')!;
      const party = read.party('party');
      if (read.flag('against_dues')) {
        line(read.account('payable')!, amount, 'payable', false, party);
        line(read.account('paid_from')!, -amount, 'paid_from', true);
        return out;
      }
      // An expense with tax you can claim back (L43): the expense at its taxable value, the tax, and what was paid.
      const taxed = taxOf(ctx, read, 'input', [{ minor: amount, discount: 0n, rate: rateOf(read), hsn: null }], { document: false, on });
      line(read.account('account')!, taxed?.lineValues[0] ?? amount, 'account', false, party);
      for (const head of taxed?.heads ?? []) out.push(head);
      line(read.account('paid_from')!, -out.reduce((sum, one) => sum + one.minor, 0n), 'paid_from', true);
      return out;
    }
    case 'sale': {
      const amount = read.amount('amount')!;
      const { value, taxed } = documentTax(ctx, read, 'output', amount, on);
      const gross: Working[] = [{ account: read.account('income')!, minor: -value, party: null, on, role: 'income', main: false }];
      documentLines(1n, read, on, out, { kind: 'sale', counterRole: 'receivable', gross, taxed });
      const cost = read.amount('cost');
      if (cost !== null && cost !== 0n) {
        const account = read.account('cost_account') ?? stop('RULE_ROLE_MISSING', 'cost_account', null, true);
        line(account, cost, 'cost_account', false);
        line(read.account('cost_payable')!, -cost, 'cost_payable', false, read.party('cost_party'));
      }
      const rate = read.percent('commission_rate');
      const commission = read.amount('commission') ?? (rate === null ? null : percentOf(amount, rate));
      if (commission !== null && commission !== 0n) {
        const account = read.account('commission_account') ?? stop('RULE_ROLE_MISSING', 'commission_account', null, true);
        const payable = read.account('commission_payable') ?? stop('RULE_ROLE_MISSING', 'commission_payable', null, true);
        line(account, commission, 'commission_account', false);
        line(payable, -commission, 'commission_payable', false);
      }
      return out;
    }
    case 'purchase': {
      const amount = read.amount('amount')!;
      const { value, taxed } = documentTax(ctx, read, 'input', amount, on);
      const gross: Working[] = [{ account: read.account('expense')!, minor: value, party: null, on, role: 'expense', main: false }];
      documentLines(-1n, read, on, out, { kind: 'purchase', counterRole: 'payable', gross, taxed });
      return out;
    }
    case 'receipt': {
      const amount = read.amount('amount')!;
      line(read.account('paid_to')!, amount, 'paid_to', true);
      line(read.account('receivable')!, -amount, 'receivable', false, read.party('party'));
      return out;
    }
    case 'payment': {
      const amount = read.amount('amount')!;
      line(read.account('payable')!, amount, 'payable', false, read.party('party'));
      line(read.account('paid_from')!, -amount, 'paid_from', true);
      return out;
    }
    case 'charge': {
      const party = read.party('party');
      let total = 0n;
      for (const [amountRole, incomeRole] of [
        ['amount', 'income'],
        ['amount_2', 'income_2'],
        ['amount_3', 'income_3'],
      ] as const) {
        const minor = read.amount(amountRole);
        if (minor === null || minor === 0n) continue;
        const income = read.account(incomeRole) ?? stop('RULE_ROLE_MISSING', incomeRole, null, true);
        line(income, -minor, incomeRole, false);
        total += minor;
      }
      const rate = read.percent('discount_percent');
      const discount = read.amount('discount') ?? (rate === null ? 0n : percentOf(total, rate));
      if (discount !== 0n) line(read.account('discount_account')!, discount, 'discount_account', false);
      line(read.account('receivable')!, total - discount, 'receivable', true, party);
      return out;
    }
    case 'concession': {
      // A credit note gives back the tax it was charged with (L43).
      const amount = read.amount('amount')!;
      const taxed = taxOf(ctx, read, 'output', [{ minor: amount, discount: 0n, rate: rateOf(read), hsn: null }], { document: false, on });
      line(read.account('account')!, taxed?.lineValues[0] ?? amount, 'account', false);
      for (const head of taxed?.heads ?? []) out.push(head);
      line(read.account('receivable')!, -out.reduce((sum, one) => sum + one.minor, 0n), 'receivable', true, read.party('party'));
      return out;
    }
    case 'transfer': {
      const amount = read.amount('amount')!;
      line(read.account('to')!, amount, 'to', true);
      line(read.account('from')!, -amount, 'from', false);
      return out;
    }
    case 'refund': {
      const amount = read.amount('amount')!;
      const taxed = taxOf(ctx, read, 'output', [{ minor: amount, discount: 0n, rate: rateOf(read), hsn: null }], { document: false, on });
      line(read.account('account')!, taxed?.lineValues[0] ?? amount, 'account', false, read.party('party'));
      for (const head of taxed?.heads ?? []) out.push(head);
      line(read.account('paid_from')!, -out.reduce((sum, one) => sum + one.minor, 0n), 'paid_from', true);
      return out;
    }
    case 'adjustment': {
      const amount = read.amount('amount')!;
      // An Adjustment always says why (L18).
      if ((read.text('reason') ?? '').trim() === '') stop('RULE_ROLE_MISSING', 'reason', null, false);
      line(read.account('account')!, amount, 'account', true, read.party('party'));
      line(read.account('against')!, -amount, 'against', false);
      return out;
    }
    case 'opening_balance': {
      const amount = read.amount('amount')!;
      line(read.account('account')!, amount, 'account', true, read.party('party'));
      line(read.account('equity')!, -amount, 'equity', false);
      return out;
    }
    case 'bill_lines': {
      const direction = read.text('direction') === 'purchase' ? -1n : 1n;
      const counterRole = 'counter';
      const gross: Working[] = [];
      const inputs: TaxInput[] = [];
      let lineDiscounts = 0n;
      for (const row of read.rows('lines')) {
        const minor = row.read.amount('amount') ?? rowValue(row.read.text('qty'), row.read.text('rate'), ctx.chart.digits);
        if (minor === null || minor === 0n) continue;
        const discount = row.read.amount('discount') ?? 0n;
        gross.push({ account: row.read.account('account')!, minor: -direction * minor, party: null, on, role: 'lines', main: false });
        inputs.push({ minor, discount, rate: row.read.text('tax') ?? rateOf(read), hsn: row.read.text('hsn') });
        lineDiscounts += discount;
      }
      if (gross.length === 0) return out;
      // Each row is taxed at its own rate, or the entry's, as a tax invoice works it out line by line (L49).
      const taxed = taxOf(ctx, read, direction === 1n ? 'output' : 'input', inputs, { document: true, on });
      if (taxed !== null) gross.forEach((one, i) => (one.minor = -direction * taxed.lineValues[i]!));
      // The counter is the receivable on a sale and the payable on a purchase, unless the rule names one.
      const fallbackCounter = direction === 1n ? 'receivable' : 'payable';
      const withCounter = readCounter(read, counterRole, fallbackCounter);
      if (lineDiscounts !== 0n) {
        const account = read.account('discount_account') ?? stop('RULE_ROLE_MISSING', 'discount_account', null, true);
        gross.push({ account, minor: direction * lineDiscounts, party: null, on, role: 'discount_account', main: false });
      }
      documentLines(direction as 1n | -1n, withCounter, on, out, { kind: 'bill', counterRole, gross, taxed });
      return out;
    }
    case 'tax_payment': {
      // Each head collected Dr, each credit set off Cr, and the money paid Cr for the net (L43).
      const output = read.amount('output_amount') ?? 0n;
      const input = read.amount('input_amount') ?? 0n;
      if (output !== 0n) line(read.account('output_account')!, output, 'output_account', false);
      if (input !== 0n) line(read.account('input_account')!, -input, 'input_account', false);
      for (const side of ['output', 'input'] as const) {
        for (const head of ['cgst', 'sgst', 'utgst', 'igst'] as const) {
          const role = `${side}_${head}`;
          const minor = read.amount(role) ?? 0n;
          if (minor === 0n) continue;
          const account = ctx.chart.bySystemKey[taxAccountKey(side, head)] ?? stop('RULE_ACCOUNT_UNKNOWN', role, null, true);
          line(account, side === 'output' ? minor : -minor, role, false);
        }
      }
      line(read.account('paid_from')!, -out.reduce((sum, one) => sum + one.minor, 0n), 'paid_from', true);
      return out;
    }
    case 'charge_plan':
      // A plan says what to bill and when (L23); the charges it writes post through their own rule.
      return out;
    case 'quantity':
      // Only quantity moves (Phase 9): its lines come from the rule's quantity part.
      return out;
  }
};

/* ---------------------------------------------------------- the quantities */

/** Quantities are worked at six decimal places: enough for 0.25 kg and 1.5 litres, exact. */
const QTY_DIGITS = 6;

const trimDecimal = (text: string): string => (text.includes('.') ? text.replace(/\.?0+$/, '') : text);

const sameUnit = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** The accounts each move posts to: the debit, then the credit (D3). */
const MOVE_ACCOUNTS: Readonly<Record<QuantityMove, readonly [string, string]>> = {
  in: ['stock', 'stock_in'],
  out: ['stock_out', 'stock'],
  loss: ['stock_loss_qty', 'stock'],
};

/**
 * The quantity lines of one entry (Phase 9, D3): for each item it moves, two
 * lines in the item's base unit, the debit and the credit of its move, each
 * carrying the item. A pack quantity is turned into base units here (2 peti
 * of 12 pcs is 24 pcs), and the line keeps what was typed (`dimensions.said`).
 * An entry that names neither an item nor a quantity moves none; one that
 * names one without the other is refused under the empty field.
 */
const quantityLines = (
  part: QuantityPart,
  values: LedgerValues,
  chart: LedgerChart,
  on: string,
  dimensions: Readonly<Record<string, unknown>>,
): DraftLine[] => {
  const rows: readonly LedgerValues[] =
    part.lines === undefined
      ? [values]
      : (Array.isArray(values[part.lines]) ? (values[part.lines] as unknown[]) : []).filter(
          (row): row is LedgerValues => typeof row === 'object' && row !== null,
        );
  const [debitKey, creditKey] = MOVE_ACCOUNTS[part.move];
  const out: DraftLine[] = [];
  for (const row of rows) {
    const itemValue = row[part.item];
    const qtyValue = row[part.qty];
    if (isEmpty(itemValue) && isEmpty(qtyValue)) continue;
    if (isEmpty(itemValue)) stop('RULE_ROLE_MISSING', 'item', part.item, false);
    if (isEmpty(qtyValue)) stop('RULE_ROLE_MISSING', 'qty', part.qty, false);
    const item = asId(itemValue);
    const facts = item === null ? undefined : chart.items?.[item.toLowerCase()];
    // A pick that is no row of a stock items list: the rule reads the wrong field (L58).
    if (item === null || facts === undefined) return stop('RULE_ACCOUNT_UNKNOWN', 'item', part.item, true);
    const typed = toMinor(qtyValue as string | number, QTY_DIGITS);
    if (typed === null) return stop('RULE_ROLE_MISSING', 'qty', part.qty, false);
    if (typed === 0n) continue;
    const unitValue = part.unit === undefined ? undefined : row[part.unit];
    const saidRaw = isEmpty(unitValue) ? null : (asId(unitValue) ?? String(unitValue));
    const said = saidRaw === null ? null : (chart.unitWords?.[saidRaw.toLowerCase()] ?? saidRaw);
    const converted = said !== null && !sameUnit(said, facts.unit);
    let base = typed;
    if (converted) {
      // A unit the item does not know: the person says it again, under the field.
      if (facts.pack === null || !sameUnit(said, facts.pack.unit)) return stop('RULE_ROLE_MISSING', 'unit', part.unit ?? null, false);
      const size = toMinor(facts.pack.size, QTY_DIGITS);
      if (size === null || size <= 0n) return stop('RULE_ROLE_MISSING', 'unit', part.unit ?? null, false);
      base = (typed * size) / 10n ** BigInt(QTY_DIGITS);
    }
    if (base === 0n) continue;
    const debit = chart.bySystemKey[debitKey] ?? stop('RULE_ACCOUNT_UNKNOWN', 'quantity', null, true);
    const credit = chart.bySystemKey[creditKey] ?? stop('RULE_ACCOUNT_UNKNOWN', 'quantity', null, true);
    const kept = converted ? { ...dimensions, said: { qty: trimDecimal(minorToDecimal(typed, QTY_DIGITS)), unit: said } } : dimensions;
    const line = (account: string, minor: bigint): DraftLine => ({
      account,
      amount: trimDecimal(minorToDecimal(minor, QTY_DIGITS)),
      unit: facts.unit,
      party: null,
      on,
      role: 'quantity',
      main: false,
      dimensions: kept,
      item: item.toLowerCase(),
    });
    out.push(line(debit, base), line(credit, -base));
  }
  return out;
};

/** A reader whose `counter` falls back to the receivable or the payable by its system key. */
const readCounter = (read: Reader, role: string, systemKey: string): Reader => ({
  ...read,
  account: (asked: string) => {
    if (asked !== role) return read.account(asked);
    return read.account(role) ?? read.account(`__${systemKey}`) ?? stop('RULE_ACCOUNT_UNKNOWN', role, null, true);
  },
});

/**
 * The lines one entry posts, or the problem that stops it.
 *
 * `map.by_choice` picks the pattern and its roles from the entry's own choice
 * (L44); its roles lay over `map.roles`. Every line keeps the dimension
 * fields the map names and the operating unit (L41). A line on an account
 * whose lines get matched, with no party, posts to To sort instead (L37). A
 * line of nought is dropped, and an entry of nought posts nothing.
 */
export const buildLines = (
  pattern: PostingPattern | string,
  map: PostingMap,
  values: LedgerValues,
  chart: LedgerChart,
  options: BuildOptions,
): BuildResult => {
  try {
    let chosen = pattern;
    let roles: Readonly<Record<string, RoleSource>> = map.roles ?? {};
    let quantity: QuantityPart | null = map.quantity ?? null;
    if (map.by_choice !== undefined) {
      const picked = values[map.by_choice.field];
      if (isEmpty(picked)) stop('RULE_ROLE_MISSING', 'by_choice', map.by_choice.field, false);
      const option = map.by_choice.options[asId(picked) ?? String(picked)];
      if (option?.pattern === undefined) return { ok: false, problem: { code: 'RULE_CHOICE_UNMAPPED', role: 'by_choice', fieldId: map.by_choice.field, broken: true } };
      chosen = option.pattern;
      roles = { ...roles, ...(option.roles ?? {}) };
      if (option.quantity !== undefined) quantity = option.quantity;
    }
    if (!isPostingPattern(chosen)) return { ok: false, problem: { code: 'RULE_PATTERN_UNKNOWN', role: null, fieldId: null, broken: true } };

    const defs: Record<string, RoleDef> = {
      ...PATTERN_ROLES[chosen],
      // The two counters bill_lines falls back to.
      __receivable: R('account', false, KEY('receivable')),
      __payable: R('account', false, KEY('payable')),
    };
    const read = readerOf(defs, roles, values, chart);
    const working = templateLines(chosen, read, options.on, { chart, tax: options.tax }).filter((one) => one.minor !== 0n);

    const dimensions: Record<string, unknown> = {};
    for (const fieldId of map.dimensions ?? []) {
      const held = values[fieldId];
      if (!isEmpty(held)) dimensions[fieldId] = held;
    }
    for (const [key, held] of Object.entries(options.dimensions ?? {})) {
      if (!isEmpty(held)) dimensions[key] = held;
    }

    const toSort = (): string => chart.bySystemKey.to_sort ?? stop('RULE_ACCOUNT_UNKNOWN', 'to_sort', null, true);
    const lines: DraftLine[] = working.map((one) => {
      const noParty = one.party === null && chart.openItem.has(one.account);
      return {
        account: noParty ? toSort() : one.account,
        amount: minorToDecimal(one.minor, chart.digits),
        unit: chart.unit,
        party: one.party,
        on: one.on,
        role: one.role,
        main: one.main,
        dimensions: one.extra === undefined ? dimensions : { ...dimensions, ...one.extra },
        ...(noParty ? { noParty: true as const } : {}),
      };
    });
    // The quantity it moves, in the same posting as its money (Phase 9, D3).
    if (quantity !== null) lines.push(...quantityLines(quantity, values, chart, options.on, dimensions));
    return { ok: true, pattern: chosen, lines };
  } catch (error) {
    if (error instanceof Stop) return { ok: false, problem: error.problem };
    throw error;
  }
};

/* ------------------------------------------------------------ the balance */

/** Exact decimal to a scaled integer at nine places, enough for any currency and any quantity. */
const SCALE = 9;

export type BalanceCheck =
  | { readonly ok: true }
  | { readonly ok: false; readonly code: 'POSTING_ONE_LINE' }
  | { readonly ok: false; readonly code: 'POSTING_UNBALANCED'; readonly unit: string; readonly off: string };

/**
 * L8: a posting has at least two lines, and its lines sum to nought for each
 * unit. The server asks this before the insert, so the refusal can name what
 * is off; a deferred trigger asks it again at commit.
 */
export const checkBalanced = (lines: readonly { readonly amount: string | number; readonly unit: string }[]): BalanceCheck => {
  if (lines.length < 2) return { ok: false, code: 'POSTING_ONE_LINE' };
  const sums = new Map<string, bigint>();
  for (const line of lines) {
    const minor = toMinor(line.amount, SCALE) ?? 0n;
    sums.set(line.unit, (sums.get(line.unit) ?? 0n) + minor);
  }
  for (const [unit, sum] of sums) {
    if (sum !== 0n) return { ok: false, code: 'POSTING_UNBALANCED', unit, off: minorToDecimal(sum, SCALE).replace(/\.?0+$/, '') };
  }
  return { ok: true };
};

/** The lines of one entry grouped by the day each posts on: one posting per day, each balanced on its own (L48). */
export const postingsByDay = (lines: readonly DraftLine[]): { readonly on: string; readonly lines: readonly DraftLine[] }[] => {
  const days = new Map<string, DraftLine[]>();
  for (const line of lines) days.set(line.on, [...(days.get(line.on) ?? []), line]);
  return [...days.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([on, held]) => ({ on, lines: held }));
};

/* --------------------------------------------------------------- the rule */

/**
 * A rule checked when it is saved: a pattern of the closed set, every role it
 * maps one the pattern has, every required role mapped or defaulted (a rule
 * with no amount role is refused here), and every option of a `by_choice` a
 * pattern of its own (L44).
 */
export const checkRule = (pattern: string, map: PostingMap): readonly LedgerProblem[] => {
  const problems: LedgerProblem[] = [];
  const checkRoles = (chosen: string, roles: Readonly<Record<string, RoleSource>>): void => {
    if (!isPostingPattern(chosen)) {
      problems.push({ code: 'RULE_PATTERN_UNKNOWN', role: null, fieldId: null, broken: true });
      return;
    }
    const defs = PATTERN_ROLES[chosen];
    for (const [role, source] of Object.entries(roles)) {
      if (defs[role] === undefined) problems.push({ code: 'RULE_ROLE_UNKNOWN', role, fieldId: null, broken: true });
      // Only a party may be the entry itself (L46).
      else if ('self' in source && defs[role]!.kind !== 'party') problems.push({ code: 'RULE_ROLE_UNKNOWN', role, fieldId: null, broken: true });
    }
    for (const [role, def] of Object.entries(defs)) {
      if (def.required && def.default === undefined && roles[role] === undefined) {
        problems.push({ code: 'RULE_ROLE_MISSING', role, fieldId: null, broken: true });
      }
    }
  };
  // A record that moves only quantity says what moves (Phase 9).
  const checkQuantity = (chosen: string, part: QuantityPart | null | undefined): void => {
    if (chosen === 'quantity' && (part === null || part === undefined)) problems.push({ code: 'RULE_ROLE_MISSING', role: 'quantity', fieldId: null, broken: true });
  };
  if (map.by_choice === undefined) {
    checkRoles(pattern, map.roles ?? {});
    checkQuantity(pattern, map.quantity);
    return problems;
  }
  for (const [key, option] of Object.entries(map.by_choice.options)) {
    if (option.pattern === undefined) {
      problems.push({ code: 'RULE_CHOICE_UNMAPPED', role: key, fieldId: map.by_choice.field, broken: true });
      continue;
    }
    checkRoles(option.pattern, { ...(map.roles ?? {}), ...(option.roles ?? {}) });
    checkQuantity(option.pattern, option.quantity === undefined ? map.quantity : option.quantity);
  }
  return problems;
};

/**
 * Where a money-in form keeps "Is this against the dues?" (L24): the field the
 * rule's `against_dues` role reads (the hidden yes-or-no field named
 * against_dues) and the field its `party` role reads. Null for a role the
 * rule does not read from a field: such a form never asks.
 */
export const duesQuestionFields = (map: PostingMap): { readonly againstDues: string | null; readonly party: string | null } => {
  const fieldOf = (role: string): string | null => {
    const source = map.roles?.[role];
    return source !== undefined && 'field' in source ? source.field : null;
  };
  return { againstDues: fieldOf('against_dues'), party: fieldOf('party') };
};

/** The fields a rule reads: an edit that changes none of them posts nothing new (L11). */
export const fieldsRead = (map: PostingMap): readonly string[] => {
  const found = new Set<string>();
  const walk = (source: RoleSource | undefined): void => {
    if (source === undefined || !('field' in source)) return;
    found.add(source.field);
  };
  for (const source of Object.values(map.roles ?? {})) walk(source);
  // The fields a quantity part reads: an edit of the litres moves the stock again.
  const quantity = (part: QuantityPart | null | undefined): void => {
    if (part === null || part === undefined) return;
    for (const fieldId of [part.lines, part.item, part.qty, part.unit]) if (fieldId !== undefined) found.add(fieldId);
  };
  quantity(map.quantity);
  if (map.by_choice !== undefined) {
    found.add(map.by_choice.field);
    for (const option of Object.values(map.by_choice.options)) {
      for (const source of Object.values(option.roles ?? {})) walk(source);
      quantity(option.quantity);
    }
  }
  for (const fieldId of map.dimensions ?? []) found.add(fieldId);
  return [...found];
};

/** Whether a rule maps a tax role anywhere: only then does the server read the workspace's tax for it (L43). */
export const ruleTaxes = (map: PostingMap): boolean => {
  const taxes = (roles: Readonly<Record<string, RoleSource>> | undefined): boolean => {
    if (roles === undefined) return false;
    if (roles.tax !== undefined || roles.tax_amount !== undefined) return true;
    return Object.values(roles).some((source) => 'field' in source && source.each?.tax !== undefined);
  };
  return taxes(map.roles) || Object.values(map.by_choice?.options ?? {}).some((option) => taxes(option.roles));
};

/** The party ids an entry names through its rule's `party` role, lower case: the rows whose Tax ID and State the server reads. */
export const partyIdsOf = (map: PostingMap, values: LedgerValues): readonly string[] => {
  const found = new Set<string>();
  const add = (source: RoleSource | undefined): void => {
    if (source === undefined || !('field' in source)) return;
    const id = asId(values[source.field]);
    if (id !== null) found.add(id.toLowerCase());
  };
  add(map.roles?.party);
  for (const option of Object.values(map.by_choice?.options ?? {})) add(option.roles?.party);
  return [...found];
};

/**
 * Whether a rule makes each entry its own party (L46): its `party` role is
 * `{ self }`, on the rule or on any option of its choice. Such an entry is
 * owed on as a row of a list is, so its page reads its statement.
 */
export const ruleSelfParty = (map: PostingMap): boolean => {
  const self = (source: RoleSource | undefined): boolean => source !== undefined && 'self' in source && source.self === true;
  return self(map.roles?.party) || Object.values(map.by_choice?.options ?? {}).some((option) => self(option.roles?.party));
};

/**
 * Which way one entry's money moves under its record's rule (L50): what the
 * grant fences `in_only` and `out_only` read in place of the entry's sign. 1
 * in, -1 out, 0 neither (a transfer, an adjustment, an opening balance), null
 * when the rule cannot say yet (a both-ways record whose choice is empty).
 *
 * The pattern decides, or the option of a both-ways record (L44); a bill's
 * lines read their `direction` role; a negative amount turns it, as a
 * negative amount under a cash-in choice moves cash out.
 */
export const directionOfEntry = (pattern: PostingPattern | string, map: PostingMap, values: LedgerValues): 1 | -1 | 0 | null => {
  let chosen: string = pattern;
  let roles: Readonly<Record<string, RoleSource>> = map.roles ?? {};
  if (map.by_choice !== undefined) {
    const picked = values[map.by_choice.field];
    if (isEmpty(picked)) return null;
    const option = map.by_choice.options[asId(picked) ?? String(picked)];
    if (option?.pattern === undefined) return null;
    chosen = option.pattern;
    roles = { ...roles, ...(option.roles ?? {}) };
  }
  if (!isPostingPattern(chosen)) return null;
  let direction: number = PATTERN_DIRECTION[chosen];
  if (chosen === 'bill_lines') {
    const source = roles.direction;
    const said = source === undefined ? null : 'const' in source ? source.const : 'field' in source ? values[source.field] : null;
    if (said === 'purchase') direction = -1;
  }
  const amount = roles.amount;
  if (amount !== undefined && 'field' in amount) {
    const held = values[amount.field];
    const figure = typeof held === 'number' ? held : typeof held === 'string' ? Number(held) : NaN;
    if (Number.isFinite(figure) && figure < 0) direction = -direction;
  }
  return direction === 0 ? 0 : direction > 0 ? 1 : -1;
};

/** Every quantity part a rule holds, its own and each choice's (Phase 9): the fields the server reads stock items and units through. */
export const quantityPartsOf = (map: PostingMap): readonly QuantityPart[] => {
  const parts: QuantityPart[] = [];
  if (map.quantity !== undefined) parts.push(map.quantity);
  for (const option of Object.values(map.by_choice?.options ?? {})) if (option.quantity !== undefined && option.quantity !== null) parts.push(option.quantity);
  return parts;
};

/** The stock item ids (lower case) an entry names through its rule's quantity parts: the rows the server reads units from. */
export const itemIdsOf = (map: PostingMap, values: LedgerValues): readonly string[] => {
  const found = new Set<string>();
  for (const part of quantityPartsOf(map)) {
    const rows = part.lines === undefined ? [values] : Array.isArray(values[part.lines]) ? (values[part.lines] as unknown[]) : [];
    for (const row of rows) {
      if (typeof row !== 'object' || row === null) continue;
      const id = asId((row as LedgerValues)[part.item]);
      if (id !== null) found.add(id.toLowerCase());
    }
  }
  return [...found];
};
